import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../services/friendlyApi';
import type { PaymentAllocationCreate, PaymentCreate, ListPaymentsParams } from '../types';
import { ACCOUNTS_QUERY_KEY } from './useAccounts';
import { INVOICES_QUERY_KEY } from './useInvoices';
import { BILLS_QUERY_KEY } from './useBills';
import { getDocumentDistributionsQueryKey } from '../../../shared/documents';
import type { DocumentDistributePayload } from '../../../shared/documents';

export const PAYMENTS_QUERY_KEY = 'payments';

export function usePayments(params?: ListPaymentsParams) {
  return useQuery({
    queryKey: [PAYMENTS_QUERY_KEY, params],
    queryFn: () => paymentApi.list(params),
  });
}

export function usePayment(id: string) {
  return useQuery({
    queryKey: [PAYMENTS_QUERY_KEY, id],
    queryFn: () => paymentApi.get(id),
    enabled: !!id,
  });
}

/** Payments applied to a specific invoice — the detail-page history section. */
export function useInvoicePayments(invoiceId: string | undefined) {
  return useQuery({
    queryKey: [PAYMENTS_QUERY_KEY, 'for-invoice', invoiceId],
    queryFn: () => paymentApi.listForInvoice(invoiceId as string),
    enabled: !!invoiceId,
  });
}

export function useCreatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PaymentCreate) => paymentApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
      // An allocated payment settles its invoice(s) (received) or bill(s) (made):
      // refresh both balances + history so the detail pages don't go stale.
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useVoidPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => paymentApi.void(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
      // Voiding a payment re-opens its invoice(s)/bill(s): refresh balances +
      // history so the detail pages' amount_due and payment section don't go stale.
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

/**
 * Apply an existing vendor (made) payment to open bills after creation (the AP
 * "apply afterwards" path). Settles the bill(s) — refresh bill balances,
 * payment history, and cash accounts.
 */
export function useAllocatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, allocations }: { id: string; allocations: PaymentAllocationCreate[] }) =>
      paymentApi.allocate(id, allocations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

/**
 * Email / re-share a receipt for a received payment to the payer. Refreshes the
 * document distribution history so the send log updates without a reload.
 */
export function useDistributePayment(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DocumentDistributePayload) => paymentApi.distribute(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getDocumentDistributionsQueryKey() });
    },
  });
}
